import {Injectable} from "@angular/core";
import {ProductBackorderStatus, ProductStockStatus, ProductTypes} from "../../ecommerce-product.enum";
import {IEcommerceProduct, Image, IMedia} from "../../ecommerce-product.interface";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {EcommerceService} from "../../ecommerce.service";
import {
  IAddProductForm,
  IAttribute, ICategory,
  IOption, ITaxClass, ITemplate,
  IVariantsForm,
  IVariantsUpload,
  TaxStatus
} from "./add-product-form.interface";
import {Utils} from "../../../../../shared/utils/utils";
import {BehaviorSubject, combineLatest} from "rxjs";
import {filter, map} from "rxjs/operators";
import {TextUtils} from "../../../../../shared/utils/text-utils";


interface IFilteredImages {
  imageArray: any [];
  imagesWithFile: any[];
  imagesToUpload: any;
}

interface IVariantsRequest {
  create: IVariantsUpload[];
  update: IVariantsUpload[];
}

@Injectable()
export class ProductAddHelperService {
  priceValidation = [Validators.min(0)];
  measurements: BehaviorSubject<{ weight_unit: string; dimension_unit: string; }> =
    new BehaviorSubject<{ weight_unit: string; dimension_unit: string }>({
      weight_unit: 'kg',
      dimension_unit: "cm"
    });
  currency: BehaviorSubject<{ symbol: string; }> =
    new BehaviorSubject<{ symbol: string; }>({symbol: '$'});
  images: IMedia[] = [];

  constructor(private _fb: FormBuilder,
              private _ecommerceService: EcommerceService) {
  }

  processFormData(form: IAddProductForm, images: any[], attributes?: any[]): Partial<IEcommerceProduct> {

    const uploadObj: Partial<IEcommerceProduct> = {};

    uploadObj.type = form.type;
    if (form.description_group) {
      uploadObj.name = form.description_group.name;
      uploadObj.description = this.applyInlineStylesBasedOnClasses(form.description_group.long_description);
      uploadObj.short_description = this.applyInlineStylesBasedOnClasses(form.description_group.short_description);
      uploadObj.status = form.description_group.status === 'active' || form.description_group.status === 'publish' ? 'publish' : 'draft';
    }

    if (form.pricing_group) {

      uploadObj.sale_price = TextUtils.pointToDecimal(Utils.toNumber(form.pricing_group.sale_price.replace(this.currency.value.symbol, '')));
      uploadObj.price = TextUtils.pointToDecimal(Utils.toNumber(form.pricing_group.price.replace(this.currency.value.symbol, '')));
      uploadObj.regular_price = TextUtils.pointToDecimal(Utils.toNumber(form.pricing_group.price.replace(this.currency.value.symbol, '')));
      uploadObj.tax_status = form.pricing_group.charge_tax ? TaxStatus.TAXABLE : TaxStatus.NONE;
    }

    if (form.inventory_group) {
      uploadObj.stock_status = form.inventory_group.stock_status as ProductStockStatus;
      uploadObj.backorders_allowed = form.inventory_group.backorder_status === ProductBackorderStatus.ALLOW;
      uploadObj.backorders = form.inventory_group.backorder_status;
      uploadObj.stock_quantity = +form.inventory_group.quantity;
      uploadObj.manage_stock = !!form.inventory_group.track_quantity;
    }

    if (form.shipping_group) {
      uploadObj.weight = TextUtils.pointToDecimal(Utils.toNumber(form.shipping_group.weight));

      if (form.shipping_group.shipping_class !== null) {
        uploadObj.shipping_class = form.shipping_group.shipping_class;
      }

      uploadObj.dimensions = {
        width: TextUtils.pointToDecimal(Utils.toNumber(form.shipping_group.width)),
        height: TextUtils.pointToDecimal(Utils.toNumber(form.shipping_group.height)),
        length: TextUtils.pointToDecimal(Utils.toNumber(form.shipping_group.length))
      };
    }

    if (images.length) {
      images = images.map(image => {
        return {id: image.id};
      });
      uploadObj.images = images;
    }

    if (form.category_group) {
      if (form.category_group.template.id) {
        uploadObj.template_id = form.category_group.template.id;
      }
      if (form.category_group.category.id) {
        uploadObj.categories = [{
          id: form.category_group.category.id
        }];
      }

      if (form.category_group.subcategory.id) {
        uploadObj.categories.push({
          id: form.category_group.subcategory.id
        });
      }

      if (form.category_group.collection.length) {
        uploadObj.tags = form.category_group.collection.map(({id}) => ({id}));
      }
    }

    if (form.linked_products_group) {
      if (form.linked_products_group.upsells.length) {
        uploadObj.upsell_ids = this._filterIds(form.linked_products_group.upsells);
      }

      if (form.linked_products_group.cross_sells.length) {
        uploadObj.cross_sell_ids = this._filterIds(form.linked_products_group.cross_sells);
      }
    }

    if (attributes) {
      uploadObj.attributes = attributes;
    }

    if (uploadObj.attributes && uploadObj.attributes.length) {
      uploadObj.type = ProductTypes.VARIABLE;
    }

    if (form.tax_class) {
      uploadObj.tax_class = form.tax_class.slug;
    }

    return uploadObj;
  }

  applyInlineStylesBasedOnClasses(htmlString) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const alignElements = tempDiv.querySelectorAll('.ql-align-center, .ql-align-left, .ql-align-right');

    alignElements.forEach((element) => {
      // @ts-ignore
      if (element.style) {
        if (element.classList.contains('ql-align-center')) {
          // @ts-ignore
          element.style.textAlign = 'center';
        } else if (element.classList.contains('ql-align-left')) {
          // @ts-ignore
          element.style.textAlign = 'left';
        } else if (element.classList.contains('ql-align-right')) {
          // @ts-ignore
          element.style.textAlign = 'right';
        }
      }
    });

    // Return the updated HTML string
    return tempDiv.innerHTML;
  }

  filterImages(form: IAddProductForm): IFilteredImages {
    const imageArray = [];
    const imagesWithFile = [];
    const imagesToUpload = {};
    form.media_group.images.map((image) => {
      if (image.id) {
        imageArray.push({id: image.id, order: image.order});
      } else {
        imagesWithFile.push(image);
        imagesToUpload[image.order] = image.file;
      }
    });
    return {
      imageArray,
      imagesWithFile,
      imagesToUpload
    };
  }

  createImageControl(image: Image, order: number = 0): FormGroup {
    return this._fb.group({
      ...image,
      order
    });
  }

  addAttributeIdInOptions(attributes: IAttribute[], options: IOption[]) {
    attributes.forEach((attr: IAttribute) => {
      const selectedOption = options.find(option => option.option.label === attr.name);
      if (selectedOption) {
        selectedOption.option.value = attr.id;
        selectedOption.option_value.forEach((value) => value.line_id = attr.id);
      }
    });
  }

  processVariantsFormData(form: IVariantsForm[]): IVariantsRequest {
    const variations: IVariantsRequest = {update: [], create: []} as IVariantsRequest;
    form.forEach(item => {
      const variationsUploadData: IVariantsUpload = {} as IVariantsUpload;
      variationsUploadData.attributes = item.options.map(option => {
        return {
          id: option.line_id,
          option: option.name
        };
      });
      variationsUploadData.price = TextUtils.pointToDecimal(Utils.toNumber(item.price?.replace(this.currency.value.symbol, '')));
      variationsUploadData.sale_price = TextUtils.pointToDecimal(Utils.toNumber(item.sale_price?.replace(this.currency.value.symbol, '')));
      variationsUploadData.regular_price = TextUtils.pointToDecimal(Utils.toNumber(item.price?.replace(this.currency.value.symbol, '')));
      variationsUploadData.dimensions = {
        width: item.width,
        height: item.height,
        length: item.length,
      };
      variationsUploadData.weight = item.weight;
      variationsUploadData.stock_status = item.stock_status;
      variationsUploadData.backorders = item.backorder_status;
      variationsUploadData.sku = item.sku;
      variationsUploadData.image = item.image;
      variationsUploadData.status = item.status;
      variationsUploadData.stock_quantity = item.stock_quantity;
      variationsUploadData.manage_stock = !!item.stock_quantity;

      if (item.id) {
        variationsUploadData.id = item.id;
        variations.update.push(variationsUploadData);
      } else {
        variations.create.push(variationsUploadData);
      }
    });
    return variations;
  }

  processAttributesForm(variants) {
    const attributesWithValues: IOption[] = variants.filter((variant, i) => variant.option_value && variant.option && variant.option.label && variant.option_value.length);
    return attributesWithValues.map((item, index) => {
      return {
        id: item.option.value,
        visible: true,
        variation: true,
        position: index,
        options: item.option_value.map(opt => opt.name)
      };
    });
  }

  sortCategories(selectedCategories: any[], allCategories: ICategory[]) {
    const fullCategories = selectedCategories.map(cat => allCategories.find(c => c.id === cat.id));
    const categoriesObj = {
      category: {},
      subcategory: {}
    };

    fullCategories.forEach(cat => {
      if (!cat) {
        return;
      }
      if (cat.parent) {
        categoriesObj.subcategory = {id: cat.id, label: cat.name};
      } else {
        categoriesObj.category = {id: cat.id, label: cat.name};
      }
    });

    return categoriesObj;
  }

  sortTemplates(selectedTemplate: number, allTemplates: ITemplate[]) {
    const fullTemplates = allTemplates?.find(c => c.id === selectedTemplate);

    return {id: fullTemplates?.id, label: fullTemplates?.title};
  }

  sortTaxClasses(selectedClass: string, allTaxes: ITaxClass[]) {
    return allTaxes.find(c => c.slug === selectedClass);
  }

  transformProductToForm(prod: IEcommerceProduct, products: IEcommerceProduct[]) {
    return {
      description_group: {
        type: prod.type,
        status: prod.status,
        name: prod.name,
        short_description: prod.short_description,
        long_description: prod.description
      },
      media_group: {
        images: prod.images
      },
      category_group: {
        collection: prod.tags,
        category: prod.category,
        subcategory: prod.subcategory,
        template: prod.template
      },
      pricing_group: {
        price: Utils.toNumber(prod.regular_price),
        sale_price: Utils.toNumber(prod.sale_price),
        charge_tax: prod.tax_status === TaxStatus.TAXABLE,
        tax_percent: prod.tax_class
      },
      inventory_group: {
        stock_status: prod.stock_status,
        track_quantity: prod.manage_stock,
        backorder_status: prod.backorders,
        quantity: prod.stock_quantity
      },
      linked_products_group: {
        upsells: prod.upsell_ids.map(id => products.find(pr => pr.id === id)),
        cross_sells: prod.cross_sell_ids.map(id => products.find(pr => pr.id === id))
      },
      shipping_group: {
        weight: prod.weight,
        shipping_class_id: prod.shipping_class_id,
        shipping_class: prod.shipping_class,
        ...prod.dimensions
      },
      variants_group: {
        variants: prod.attributes.map(attr => {
          return {
            option: {
              label: attr.name,
              value: attr.id
            },
            option_value: attr.options.map(opt => {
              return {
                name: opt
              };
            })
          };
        })
      },
      tax_class: {
        slug: prod.tax_class || 'standard',
        name: prod.tax_class
      }
    };
  }

  async uploadImages(formValue, websiteId: number): Promise<any[]> {
    const {imagesToUpload, imageArray, imagesWithFile} = this.filterImages(formValue);
    return await new Promise((resolve, reject) => {
      if (!imagesWithFile.length) {
        resolve(imageArray);
        return;
      }
      const observables = [];
      for (const key in imagesToUpload) {
        observables.push(this._ecommerceService.createMedia(websiteId, {[key]: imagesToUpload[key]}));
      }
      combineLatest(observables).pipe(
        filter(array => array.every(resp => !!resp && resp.response)),
        map((array) => array
          .filter(resp => resp.success)
          .map((resp, index) => {
            const keys = Object.keys(imagesToUpload);
            return {
              src: resp.data.file0.data.source_url,
              order: +keys[index].replace(/^\D+/g, ''),
              image: resp.data.file0.data
            };
          }))
      ).subscribe(files => {
        const combinedImages = [...imageArray, ...files].sort((a, b) => a.order - b.order);
        resolve(combinedImages);
      });
    });
  }

  createOptionControl(name = '', id = null): FormGroup {
    return this._fb.group({name, id});
  }

  private _filterIds(arr: any & { id: number }): number[] {
    return arr.map(({id}) => (id)).filter(id => !isNaN(id));
  }

  getProductForm(): FormGroup {
    return this._fb.group({
      type: ProductTypes.SIMPLE,
      description_group: this._fb.group({
        status: 'active',
        name: [''],
        short_description: null,
        long_description: null,
      }),
      media_group: this._fb.group({
        images: this._fb.array([])
      }),
      pricing_group: this._fb.group({
        price: new FormControl(''),
        sale_price: new FormControl(''),
        charge_tax: false,
        tax_percent: ''
      }),
      variants_group: this._fb.group({
        variants: this._fb.array([
          ...this._variantForms()
        ])
      }),
      inventory_group: this._fb.group({
        stock_status: ProductStockStatus.IN_STOCK,
        track_quantity: false,
        backorder_status: ProductBackorderStatus.DO_NOT_ALLOW,
        quantity: ''
      }),
      linked_products_group: this._fb.group({
        upsells: this._fb.array([]),
        cross_sells: this._fb.array([])
      }),
      shipping_group: this._fb.group({
        weight: '',
        weight_type: 'kg',
        length: '',
        width: '',
        height: '',
        shipping_class: null,
        shipping_class_id: null
      }),
      tax_class: this._fb.group({
        slug: '',
        name: ''
      }),
      category_group: this._fb.group({
        category: this._fb.group({
          label: '',
          id: ''
        }),
        subcategory: this._fb.group({
          label: '',
          id: ''
        }),
        collection: this._fb.array([]),
        template: this._fb.group({
          label: '',
          id: ''
        }),
      })
    });
  }

  private _variantForms() {
    return [].map(() => this._fb.group({
        option: this._fb.group({
          label: '',
          value: ''
        }),
        option_value: this._fb.array([]),
      })
    );
  }

  variantFormTemplate(): FormGroup {
    return this._fb.group({
      option: this._fb.group({
        label: '',
        value: ''
      }),
      option_value: this._fb.array([]),
    });
  }

}
